<script lang="ts" setup>
import CheckOutlineIcon from "@/components/icon/CheckOutlineIcon.vue";
import PlayIcon from "@/components/icon/playIcon.vue";
import MagicStickIcon from "@/components/icon/MagicStickIcon.vue";
import { computed, onBeforeMount, reactive, ref } from "vue";
import { useTone } from "@/stores/tone";
import { MateTone } from "@/types/entity";
import PauseIcon from "@/components/icon/pauseIcon.vue";
import { watch } from "vue";
import i18n from "@/locales";

// 常量
// const ALL_LANGUAGE_ID = 999;
const ALL_LANGUAGE_ID = 2;
const LanguageNameMap: Record<string, string> = {
  // 1: "英语",
  // 2: "中文",
  // 3: "日语",
  [ALL_LANGUAGE_ID]: i18n.global.t("Buttons.all"),
};
// props & emit
const props = defineProps<{
  modelValue: number;
  isBgm?: boolean;
  stopMusic?: number;
  toneMood?: number;
  gender?: number;
}>();
defineEmits<{
  (e: "update:modelValue", toneId: number): void;
  (e: "onEditName", id: MateTone): void;
  (e: "onDeleteMedia", id: MateTone): void;
}>();
// 内部state
const audioPlayer = ref<HTMLAudioElement | null>(null);

watch(
  () => props.stopMusic,
  () => {
    audioPlayer.value?.pause();
  }
);

interface State {
  toneInAudio: MateTone | null;
  audioStatus: "loading" | "playing" | "paused" | "ended";
  isPlaying: boolean;
  filterLanguageId: number;
}

const state = reactive<State>({
  toneInAudio: null,
  audioStatus: "ended", // loading, playing, paused, ended
  isPlaying: false,
  filterLanguageId: ALL_LANGUAGE_ID,
});
const toneList = computed(() => {
  if (props.isBgm)
    return tone.bgmList.filter((item) => item.toneMood === props.toneMood);
  if (typeof props.gender === "number")
    return tone.list
      .filter((item) => item.gender === props.gender)
      .sort((a, b) => {
        if (a.isNsfw === 1 && b.isNsfw !== 1) return -1;
        if (a.isNsfw !== 1 && b.isNsfw === 1) return 1;
        return a.name.localeCompare(b.name);
      });
  return tone.list;
});
const tone = useTone();
const loading = ref(false);
const languageOptions = computed<{ label: string; value: string }[]>(() => {
  return Object.entries(LanguageNameMap)
    .filter((item) => {
      // 过滤出有数据的选项
      const langId = Number(item[0]);
      return tone.languageSet.has(langId);
      // || langId === ALL_LANGUAGE_ID;
    })
    .map(([langId, name]) => ({
      label: name,
      value: langId,
    }));
});
// 前置请求
onBeforeMount(async () => {
  try {
    loading.value = true;
    await tone.getToneList();
    if (props.isBgm) await tone.getBgmList();
  } finally {
    loading.value = false;
  }
});
// 事件回调
const onPlay = async (tone: MateTone) => {
  state.toneInAudio = tone;
  await audioPlayer.value!.load();
  await audioPlayer.value!.play();
};
</script>
<template>
  <!-- @vue-ignore -->
  <select
    v-if="false"
    :value="state.filterLanguageId"
    class="select select-bordered"
    @change="(e) => (state.filterLanguageId = Number(e.target!.value))"
  >
    <option
      v-for="option in languageOptions"
      :key="option.value"
      :value="option.value"
    >
      {{ option.label }}
    </option>
  </select>
  <div v-if="loading">
    <div class="flex gap-2 w-full border p-2 items-center">
      <div class="skeleton-loading-bg h-10 w-10 rounded-full shrink-0"></div>
      <div class="skeleton-loading-bg w-20 h-5"></div>
    </div>
  </div>
  <template v-else>
    <div class="flex justify-between items-center border p-2 rounded">
      <div class="flex gap-2 items-center">
        <XmarkCircleIcon class="icon-2xl" />
        {{ $t("Buttons.notSetTone") }}
      </div>
      <div v-if="modelValue === 0" class="btn btn-ghost btn-sm">
        <check-outline-icon class="fill-success" />
        {{ $t("Buttons.selected") }}
      </div>
      <button
        v-else
        class="btn btn-sm rounded"
        @click="$emit('update:modelValue', 0)"
      >
        <magic-stick-icon class="icon-sm" />
        {{ $t("Buttons.select") }}
      </button>
    </div>
    <div
      v-for="tone in toneList"
      :key="tone.id"
      class="flex justify-between items-center border p-2 rounded"
    >
      <div class="flex gap-2 items-center flex-1 pr-2">
        <div v-if="tone.media" class="w-6 flex items-center justify-center">
          <pause-icon
            v-if="
              state.toneInAudio?.id === tone.id &&
              state.audioStatus === 'playing'
            "
            class="icon-2xl text-base-content"
            @click="audioPlayer?.pause()"
          />
          <div
            v-else-if="
              state.toneInAudio?.id === tone.id &&
              state.audioStatus === 'loading'
            "
            class="loading loading-dots loading-xs"
          />
          <play-icon
            v-else
            :class="{ 'fill-base-content': tone.id === modelValue }"
            class="icon-2xl"
            @click="onPlay(tone)"
          />
        </div>
        <span class="flex-1">
          {{ tone.name }}
        </span>

        <!-- 用户上传的声音 -->
        <template v-if="toneMood === 9">
          <button class="btn btn-xs rounded" @click="$emit('onEditName', tone)">
            <i class="iconify mingcute--edit-3-line text-xs" />
          </button>
          <button
            class="btn btn-xs rounded"
            @click="$emit('onDeleteMedia', tone)"
          >
            <i class="iconfont icon-delete text-xs" />
          </button>
        </template>
        <!--  性别数据不准，暂时注释  -->
        <!-- <gender-badge :gender="tone.gender === 0 ? 2 : 1" class="icon-xl" /> -->
        <span v-if="tone.isNsfw" class="text-xs text-warning"> NSFW </span>
      </div>

      <div v-if="tone.id === modelValue" class="btn btn-ghost btn-sm">
        <check-outline-icon class="fill-success" />
        {{ $t("Buttons.selected") }}
      </div>
      <button
        v-else
        class="btn btn-sm rounded"
        @click="$emit('update:modelValue', tone.id)"
      >
        <magic-stick-icon class="icon-sm" />
        {{ $t("Buttons.select") }}
      </button>
    </div>
  </template>

  <audio
    ref="audioPlayer"
    :src="state.toneInAudio?.media.url"
    class="hidden"
    controls
    @ended="state.audioStatus = 'ended'"
    @loadstart="state.audioStatus = 'loading'"
    @pause="state.audioStatus = 'paused'"
    @playing="state.audioStatus = 'playing'"
    crossorigin="anonymous"
  />
</template>
